import { useSuspenseQuery } from '@tanstack/react-query';
import { atom, useAtomValue } from 'jotai';
import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateNotFoundHeading,
} from '@gitbook/spine-react';

import { useNavigationState } from '../../navigation';
import { apiQuery } from '../../state';
import { APIErrorBoundary } from '../APIErrorBoundary';
import { useHideSplashscreen } from '../Splashscreen';
import { AccessWall } from './AccessWall';
import { PublishedContentCTAPage } from './PublishedContentCTAPage';

/**
 * Atom flipped to `true` when the user clicks "Login to edit" on the [[PublishedContentCTAPage]].
 *
 * The CTA page can be reached while logged out (via [[LoginScreenWrapper]]) as well as while
 * logged in but lacking access (via [[OrganizationAccessFallback]]). Clicking "Login to edit"
 * also signs out, which remounts a different parent and would otherwise re-trigger the CTA. We
 * persist the dismissal at the application level so the next render skips straight to the
 * fallback (login screen or org empty state).
 */
export const publishedContentCTADismissedAtom = atom(false);

/**
 * Try to resolve a published URL for the current screen and render the [[PublishedContentCTAPage]]
 * if one exists. Otherwise render the provided `fallback`.
 *
 * Detection looks at the current screen: we only attempt to resolve a CTA URL when the screen
 * carries both an `organization` and a `site`. Space-only URLs fall straight through to the
 * fallback — the published-content interstitial is a site-level concept.
 *
 * Used in two places:
 *  - [[LoginScreenWrapper]] when the user is logged out, with `fallback={<LoginScreen />}`.
 *  - [[OrganizationAccessFallback]] when the user is logged in but cannot access the org, with
 *    `fallback={<OrganizationNotFoundEmptyState />}`.
 */
export function PublishedContentFallback(props: {
    fallback: React.ReactElement;
}): React.ReactElement {
    const { fallback } = props;
    const { screen } = useNavigationState();
    const dismissed = useAtomValue(publishedContentCTADismissedAtom);

    if (dismissed) {
        return fallback;
    }

    const organizationId = 'organization' in screen ? screen.organization : undefined;
    const siteId = 'site' in screen ? screen.site : undefined;

    if (organizationId && siteId) {
        return (
            <APIErrorBoundary fallback={{ 403: fallback, 404: fallback }}>
                <SiteCTAResolver
                    organizationId={organizationId}
                    siteId={siteId}
                    fallback={fallback}
                />
            </APIErrorBoundary>
        );
    }

    return fallback;
}

/**
 * Rendered when the user is logged in but doesn't have access to the organization at the URL
 * they tried to open. Either points them at the published version of the content (if it exists)
 * or shows a generic "not found or no access" empty state.
 */
export function OrganizationAccessFallback(): React.ReactElement {
    return <PublishedContentFallback fallback={<OrganizationNotFoundEmptyState />} />;
}

function SiteCTAResolver(props: {
    organizationId: string;
    siteId: string;
    fallback: React.ReactElement;
}): React.ReactElement {
    const { data } = useSuspenseQuery(
        apiQuery('getSiteById', {
            organizationId: props.organizationId,
            siteId: props.siteId,
        })
    );

    // `urls.published` is only populated when the site has a resolvable public URL (published,
    // has a basename, not share-link-only). Fall back to the provided empty state otherwise.
    const publishedURL = data.urls.published;
    if (!publishedURL) {
        return props.fallback;
    }

    return <PublishedContentCTAPage publishedURL={publishedURL} />;
}

/**
 * Empty state rendered when the organization can't be loaded — either it doesn't exist or the
 * user has no permission to see it. The API returns the same 404 for both cases, so we collapse
 * them into a single message. Owns its full-page chrome (background + splash dismissal) so it
 * can be dropped directly into an [[APIErrorBoundary]] fallback outside any `AppLayout`.
 */
export function OrganizationNotFoundEmptyState(): React.ReactElement {
    useHideSplashscreen(0);
    return (
        <div className="flex min-h-full w-full flex-col bg-base">
            <AccessWall>
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.Organization />
                    </EmptyStateIcon>
                    <EmptyStateNotFoundHeading />
                    <Description>
                        This organization either doesn't exist or you don't have access to it.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            kind="secondary"
                            label="View organizations"
                            href={{ screen: { type: 'welcome' } }}
                        />
                    </EmptyStateActions>
                </EmptyState>
            </AccessWall>
        </div>
    );
}
